/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChecklistDto,
  ChecklistSectionBaseDto,
  ChecklistClauseDto,
  ChecklistStatus,
  ChecklistDetailsDto, ChecklistSectionDto,
} from '@gh/builder-shared-data';
import { EntityVersion, ID, WithEntityState, WithVersion } from '../../../common-shared-data';
import {
  composeSetters,
  createArrayHelper,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  fieldValidationSetter,
  fieldValidatorByEntityConfig,
  formStateSnapshot,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  hasFormStateError,
  isEntityInvalid,
  setEntityField,
  setFormStateSnapshotValue,
  TextTypes,
} from '@gh/common-shared';
import { FocusedCell } from '@gh/core-ui-grid';
import { compact, map, omit, pick, uniqueId } from 'lodash';
import { createTreeHelper } from '@gh/core-ui-ext/tree';
import { WithOptimisticLock } from '@gh/core-data';

export type ChecklistItem = ChecklistDto;

export type ChklMasterInfo = {
  name: string;
  notEditable: boolean;
};
export enum ChklLevel { Section = 1 }

export type ChklSelection = {
  readonly level?: ChklLevel;
  readonly section?: ChklSection;
  readonly clauseFocusedCell?: FocusedCell;
};
export type ChklSectionInfo = ChecklistSectionBaseDto;
export type ChklSection = {
  id: ID;
  elementId?: ID;
  info: FormStateSnapshot<ChklSectionInfo>;
  checklistItems: ChklClause[];
  isNew?: boolean;
};

export type ChklClause = WithEntityState<ChecklistClauseDto> & { id: ID };

export type ChecklistDetails = WithOptimisticLock<WithVersion<{
  id?: ID;
  status: ChecklistStatus;
  version: EntityVersion;
  checklistLevel: number;
  isParent: boolean;
  isParentEditable?: boolean;
  parentId?: ID;
  masterInfo: FormStateSnapshot<ChklMasterInfo>;
  checklistElements: ChklSection[];
}>>;

export const CHKL_CLAUSE_ENTITY_CONFIG = {
  title: { required: true, type: TextTypes.Unlimited },
};

const newChklDetailsMasterInfo = () => formStateSnapshot({
  name: '',
  notEditable: false,
});

export const newChecklistDetails = (level: number) => <ChecklistDetails>({
  masterInfo: newChklDetailsMasterInfo(),
  checklistElements: [],
  status: ChecklistStatus.ACTIVE,
  version: 1,
  checklistLevel: level,
  isParent: true,
});

export const chklDetailsFromDto = (dto: ChecklistDetailsDto) => <ChecklistDetails>({
  id: dto.id,
  checklistLevel: dto.checklistLevel,
  version: dto.version,
  status: dto.status,
  isParent: !dto.parentId,
  parentId: dto.parentId,
  masterInfo: formStateSnapshot(pick(dto, ['name', 'notEditable'])),
  checklistElements: map(dto.checklistElements, chklSectionFromDto),
});
export const chklDetailsToDto = ({
                                  id, status, masterInfo, version, checklistLevel, checklistElements,
                                }: ChecklistDetails) => <ChecklistDetailsDto>({
  id, status, checklistLevel, version,
  ...getFormStateSnapshotValue(masterInfo),
  checklistElements: checklistElements.map(chklSectionToDto),
});
const chklSectionToDto = ({
                            elementId, isNew, info, checklistItems,
                           }: ChklSection) => <ChecklistSectionDto>({
  elementId: isNew ? void 0 : elementId, // clear ID for new section
  ...getFormStateSnapshotValue(info),
  checklistItems: checklistItems.map(chklClauseToDto),
});
const chklClauseToDto = (clause: ChklClause) => <ChecklistClauseDto>(omit(clause, ['id', 'entityState']));

const chklSectionFromDto = (dto: ChecklistSectionDto) => <ChklSection>({
  id: uniqueId('chkl-section:'),
  elementId: dto.elementId,
  info: formStateSnapshot(pick(dto, ['title', 'elementType'])),
  checklistItems: map(dto.checklistItems, (clause) => ({ ...clause, id: uniqueId('chkl-clause:') })),
});

export const toClonedChklSection = (section: ChklSection) => ({
  ...section,
  elementId: void 0,
});
export const toClonedChklDetails = (details: ChecklistDetails) => ({
  ...details,
  masterInfo: setFormStateSnapshotValue(details.masterInfo, (info) => ({ ...info, name: '' })),
  checklistElements: map(details.checklistElements, toClonedChklSection),
  id: void 0,
});

export const chklTreeHelper = createTreeHelper(['sections']);

export const chklSelectionToTreePath = ({ level, section }: ChklSelection) =>
  level ? compact([section && section.id]) : void 0;

export const getChklClauseId = (clause: ChklClause) => clause.id;
export const chklClauseArr = createArrayHelper(getChklClauseId);
export const chklClauseFieldState = fieldStateByEntityConfig(CHKL_CLAUSE_ENTITY_CONFIG);
export const chklClauseValidateField = fieldValidatorByEntityConfig(CHKL_CLAUSE_ENTITY_CONFIG);
export const setChklClauseText = composeSetters(setEntityField, fieldValidationSetter(chklClauseValidateField));

export const validateChklMasterInfo = entityValidatorByEntityConfig({
   name: { required: true },
 });

export const hasChklElementFormError = ({ info }: { info: FormStateSnapshot<any> }) =>
  hasFormStateError(info);

export const isChklSection = (element: ChklSection): element is ChklSection => element.hasOwnProperty('checklistItems');

export const hasChklSectionInvalidItem = ({ checklistItems }: ChklSection) =>
  checklistItems && checklistItems.some(isEntityInvalid);

export const hasChklElementError = (element: ChklSection) =>
  hasChklElementFormError(element) || isChklSection(element) && hasChklSectionInvalidItem(element) || false;
